import React, { Fragment, useEffect, useState } from 'react';
import { Intent } from '@blueprintjs/core';
import { ButtonSave } from 'components/buttons';
import { FormCanvas } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { FailurePanel, NotificationToaster, SuccessPanel } from 'components/notifications';
import { useQuery } from 'hooks/useQuery';
import { UnauthenticatedPage } from 'pages/layouts';
import { CompletedReviewDetail } from 'pages/employee-reviews/completed';
import { EmployeeReviewsService } from 'services';

const badRequestCode = 400;
const forbiddenCode = 403;

// The API answers a review that has already been signed off with this exact message. A 400 is
// not on its own enough to tell that case from a link the driver's email client mangled, which
// fails model binding and returns a 400 of its own, so the message is what separates the two.
const alreadySignedOffMessage = "This review has already been signed off.";

const signOffNotice = "This review will be marked as electronically signed and recorded in your driver profile under reviews.";

// One value, so no two of these can be on screen at once.
const pageStates = {
    signing: "SIGNING",
    signedOff: "SIGNEDOFF",
    alreadySignedOff: "ALREADYSIGNEDOFF",
    notFound: "NOTFOUND",
    loadFailed: "LOADFAILED"
};

// ApiRequestService.get rejects with the raw axios error, so the status sits two levels down
// and is absent altogether on a network failure.
function mapLoadFailure(error) {
    const status = error?.response?.status;

    if (status === forbiddenCode) {
        return pageStates.notFound;
    }

    if (status === badRequestCode) {
        const body = error?.response?.data;

        if (typeof body === "string" && body.includes(alreadySignedOffMessage)) {
            return pageStates.alreadySignedOff;
        }

        return pageStates.notFound;
    }

    return pageStates.loadFailed;
}

export function ReviewSignOff() {

    const query = useQuery();
    const employeeId = query.get("employee");
    const reviewId = query.get("review");
    const token = query.get("code");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pageState, setPageState] = useState(pageStates.signing);
    const [review, setReview] = useState(null);
    const [signature, setSignature] = useState("");

    useEffect(() => {
        // The three link values are the whole credential, so a link missing any of them
        // cannot identify a review and is not worth asking the API about.
        if (!employeeId || !reviewId || !token) {
            setPageState(pageStates.notFound);
            setLoading(false);
            return;
        }

        EmployeeReviewsService.getReviewSignOff(employeeId, reviewId, token).then((reviewResponse) => {
            setReview(reviewResponse);
        }, (error) => {
            const failureState = mapLoadFailure(error);
            setPageState(failureState);

            if (failureState === pageStates.loadFailed) {
                NotificationToaster.show(Intent.DANGER, "Could not load this review, please try again.");
            }
        }).finally(() => {
            setLoading(false);
        });
    }, [employeeId, reviewId, token]);

    function handleSave() {
        setSaving(true);

        EmployeeReviewsService.saveReviewSignOff({ employeeId, reviewId, token, signature }).then(() => {
            setPageState(pageStates.signedOff);
        }, () => {
            NotificationToaster.show(Intent.DANGER, "Could not sign off this review, please try again.");
        }).finally(() => {
            setSaving(false);
        });
    }

    return (
        <UnauthenticatedPage>
            <Fragment>
                <ShowHide
                    evaluator={pageState === pageStates.signedOff}
                    show={(
                        <Fragment>
                            <h2>Review Signed Off</h2>
                            <SuccessPanel>
                                <p>This review has been successfully signed off.</p>
                            </SuccessPanel>
                        </Fragment>
                    )}
                />
                <ShowHide
                    evaluator={pageState === pageStates.alreadySignedOff}
                    show={(
                        <Fragment>
                            <h2>Already Signed Off</h2>
                            <SuccessPanel>
                                <p>This review has already been signed off.</p>
                                <p>If you did not sign this review off, please contact your manager/Vision administrator immediately.</p>
                            </SuccessPanel>
                        </Fragment>
                    )}
                />
                <ShowHide
                    evaluator={pageState === pageStates.notFound}
                    show={(
                        <FailurePanel title="This review cannot be found">
                            <p>No record can be found for this review.</p>
                            <p>Please get in touch with your manager/Vision administrator to clarify.</p>
                        </FailurePanel>
                    )}
                />
                <ShowHide
                    evaluator={pageState === pageStates.loadFailed}
                    show={(
                        <FailurePanel title="This review could not be loaded">
                            <p>Something went wrong while loading this review.</p>
                            <p>Please try again, or get in touch with your manager/Vision administrator.</p>
                        </FailurePanel>
                    )}
                />
                <ShowHide
                    evaluator={pageState === pageStates.signing}
                    show={(
                        <Fragment>
                            <CompletedReviewDetail
                                review={review}
                                loading={loading}
                                linkEmployee={false}
                                showSignOff={false}
                                title="Employee Review Sign Off"
                            />
                            <div className="row">
                                <h2>Sign Off</h2>
                                <ShowHide
                                    evaluator={review?.signOff?.managerSignature != null}
                                    show={(
                                        <div className="spacer-bottom">
                                            <p className="spacer-bottom-medium"><span className="bold">Manager Signature</span> - {review?.completedBy}</p>
                                            <FormCanvas
                                                readOnly
                                                imageSource={review?.signOff?.managerSignature?.url}
                                                headingText={null}
                                                helperText={null}
                                                width="40%"
                                                showDate
                                                signedDate={review?.completedDate}
                                            />
                                        </div>
                                    )}
                                />
                                <p><span className="bold">Employee Signature</span> - {review?.employee?.name}</p>
                                <FormCanvas
                                    isLoading={loading}
                                    penColour="grey"
                                    backgroundColour="#ffffff"
                                    headingText={null}
                                    onChange={(value) => setSignature(value.split(",").pop())}
                                    showDate
                                    width="40%"
                                    />
                                <p className='spacer-bottom'>{signOffNotice}</p>
                                <ButtonSave
                                    id="review-sign-off-save"
                                    text="Sign Off"
                                    icon="tick"
                                    onClick={handleSave}
                                    loading={loading}
                                    disabled={saving}
                                    simpleDisabled={!signature}
                                />
                            </div>
                        </Fragment>
                    )}
                />
            </Fragment>
        </UnauthenticatedPage>
    );
}
